import { Camera, ArrowRight } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { compressImageFile } from '@ai-tiaozs/shared';

export function ImageUploader({
  label = '拍照 / 上传',
  onChange,
  maxWidth = 1280,
}: {
  label?: string;
  onChange: (dataUrl: string | null) => void;
  maxWidth?: number;
}) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const [reading, setReading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // 仅在 dataUrl 变化时通知上层,避免因 onChange 引用变化重复触发(会误清空表单)
  useEffect(() => {
    onChangeRef.current(dataUrl);
  }, [dataUrl]);

  useEffect(() => {
    return () => {
      if (previewUrl?.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  return (
    <div className="flex items-center gap-3">
      <label className="btn-primary cursor-pointer">
        <Camera className="mr-1 h-4 w-4" /> {label}
        <input
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={async (e) => {
            const f = e.target.files?.[0];
            if (!f) return;
            if (previewUrl?.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
            setPreviewUrl(URL.createObjectURL(f));
            setDataUrl(null);
            setReading(true);
            setError(null);
            try {
              const d = await compressImageFile(f, maxWidth);
              setDataUrl(d);
            } catch (err) {
              setError(err instanceof Error ? err.message : '读取照片失败');
            } finally {
              setReading(false);
            }
          }}
        />
      </label>
      {previewUrl && (
        <img
          src={previewUrl}
          alt="预览"
          className="h-20 w-20 rounded-md object-cover ring-1 ring-slate-200"
        />
      )}
      {reading && <span className="text-xs text-slate-500">处理中…</span>}
      {error && <span className="text-xs text-red-600">{error}</span>}
    </div>
  );
}

export function PrimaryButton({
  loading,
  loadingText = '处理中…',
  children,
  onClick,
  disabled,
  type = 'button',
}: {
  loading?: boolean;
  loadingText?: string;
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  type?: 'button' | 'submit';
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={loading || disabled}
      className="btn-primary w-full"
    >
      {loading ? (
        loadingText
      ) : (
        <>
          {children} <ArrowRight className="ml-1 h-4 w-4" />
        </>
      )}
    </button>
  );
}
